import { DialogComponent } from '@theme/dialog';

const STORAGE_PREFIX = 'newsletter-popup:';
const PENDING_KEY = 'newsletter-popup:pending';
const MOBILE_QUERY = '(max-width: 749px)';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

/**
 * Checks whether the theme editor is running.
 *
 * @returns {boolean} True when the storefront is rendered inside the theme editor.
 */
function isDesignMode() {
  return Boolean(/** @type {any} */ (window).Shopify?.designMode);
}

/**
 * A newsletter popup that opens on a delay, a scroll depth, or an exit intent, and swaps to a
 * thank you message with a promo code once the visitor subscribes.
 *
 * The subscription is submitted with `fetch` so the popup can stay open. If the request fails, the
 * form is submitted the regular way and the popup re-opens on the thank you state after the reload.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLFormElement} [form] - The customer form.
 * @property {HTMLElement} [signupPanel] - The panel holding the heading, description and form.
 * @property {HTMLElement} [successPanel] - The panel holding the thank you message.
 * @property {HTMLElement} [successHeading] - The thank you heading, focused when it is revealed.
 * @property {HTMLElement} [errorMessage] - The live region used for submission errors.
 * @property {HTMLButtonElement} [submitButton] - The subscribe button.
 * @property {HTMLElement} [promoCode] - The element holding the promo code text.
 *
 * @extends DialogComponent
 */
class NewsletterPopupComponent extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #delayTimeout;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #copyTimeout;

  /** @type {(() => void)[]} */
  #teardown = [];

  #revealed = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('dialog:close', this.#handleDialogClose);

    if (isDesignMode()) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);

      // The editor re-renders the section on every setting change, so the open state is restored
      // from the session to keep the popup visible while the merchant edits it.
      if (this.testMode || readSession(this.#editorKey) === '1') {
        this.#reveal();
        return;
      }

      // Still run the configured trigger so the popup opens on product pages in the editor.
      this.#watchForTrigger();
      return;
    }

    // The form was submitted without JavaScript (or the fetch fell back to a native submit).
    if (this.posted && readSession(PENDING_KEY) === '1') {
      removeSession(PENDING_KEY);
      this.#remember({ subscribed: true });
      this.#showSuccess({ focus: false });
      this.#reveal();
      return;
    }

    if (!this.testMode && !this.#canShow()) return;

    this.#watchForTrigger();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('dialog:close', this.#handleDialogClose);
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);

    this.#stopWatching();
    clearTimeout(this.#copyTimeout);
  }

  /**
   * Submits the newsletter form without leaving the page.
   *
   * @param {SubmitEvent} event - The submit event, delegated from the wrapper around the form.
   */
  handleSubmit = async (event) => {
    const form = this.#popupRefs.form ?? this.querySelector('form');

    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    this.#setError('');
    this.#setBusy(true);

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`Newsletter signup failed with status ${response.status}`);

      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const error = page.getElementById(`NewsletterPopupError-${this.sectionId}`);

      if (error) {
        this.#setError(error.textContent?.trim() ?? '');
        return;
      }

      this.#remember({ subscribed: true });
      this.#showSuccess();
    } catch {
      // The request never completed — let the browser submit the form and re-open on the reload.
      writeSession(PENDING_KEY, '1');
      form.submit();
    } finally {
      this.#setBusy(false);
    }
  };

  /**
   * Copies the promo code to the clipboard and confirms it on the button.
   *
   * @param {MouseEvent} event - The click event.
   */
  copyCode = async (event) => {
    const button = event.target;
    const code = this.#popupRefs.promoCode?.textContent?.trim();

    if (!(button instanceof HTMLElement) || !code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }

    const { copyLabel, copiedLabel } = button.dataset;

    button.textContent = copiedLabel ?? copyLabel ?? '';
    clearTimeout(this.#copyTimeout);
    this.#copyTimeout = setTimeout(() => {
      button.textContent = copyLabel ?? '';
    }, 2500);
  };

  /**
   * Starts listening for whichever trigger the merchant configured.
   */
  #watchForTrigger() {
    if (!this.showOnMobile && window.matchMedia(MOBILE_QUERY).matches) return;

    switch (this.trigger) {
      case 'scroll': {
        const handleScroll = () => {
          if (scrollProgress() >= this.scrollDepth) this.#reveal();
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        this.#teardown.push(() => window.removeEventListener('scroll', handleScroll));
        handleScroll();
        break;
      }

      case 'exit': {
        // Pointerless devices never fire an exit intent, so fall back to the delay there.
        if (window.matchMedia('(pointer: coarse)').matches) {
          this.#scheduleDelay();
          break;
        }

        /** @param {MouseEvent} event */
        const handleMouseOut = (event) => {
          if (event.clientY <= 0 && !event.relatedTarget) this.#reveal();
        };

        document.addEventListener('mouseout', handleMouseOut);
        this.#teardown.push(() => document.removeEventListener('mouseout', handleMouseOut));
        break;
      }

      default:
        this.#scheduleDelay();
    }
  }

  #scheduleDelay() {
    this.#delayTimeout = setTimeout(() => this.#reveal(), this.delay);
    this.#teardown.push(() => clearTimeout(this.#delayTimeout));
  }

  #stopWatching() {
    for (const teardown of this.#teardown) teardown();
    this.#teardown = [];
  }

  #reveal() {
    if (this.#revealed) return;

    this.#revealed = true;
    this.#stopWatching();

    if (isDesignMode()) writeSession(this.#editorKey, '1');

    this.showDialog();
  }

  /**
   * Swaps the signup panel for the thank you message.
   *
   * @param {object} [options] - Reveal options.
   * @param {boolean} [options.focus] - Whether to move focus to the thank you heading.
   */
  #showSuccess({ focus = true } = {}) {
    const { signupPanel, successPanel, successHeading } = this.#popupRefs;

    signupPanel?.setAttribute('hidden', '');
    successPanel?.removeAttribute('hidden');
    this.dataset.state = 'success';

    if (focus) successHeading?.focus();
  }

  /**
   * @param {boolean} busy - Whether a submission is in flight.
   */
  #setBusy(busy) {
    const { form, submitButton } = this.#popupRefs;

    form?.setAttribute('aria-busy', String(busy));

    if (submitButton) submitButton.disabled = busy;
  }

  /**
   * @param {string} message - The error to announce, or an empty string to clear it.
   */
  #setError(message) {
    const { errorMessage } = this.#popupRefs;

    if (!errorMessage) return;

    errorMessage.textContent = message;
    errorMessage.toggleAttribute('hidden', message === '');
  }

  /**
   * Whether the popup may open, based on what the visitor already did with it.
   *
   * Dismissals are stored per page type so closing the popup on the homepage does not
   * prevent it from opening on a product page.
   *
   * @returns {boolean} True when the popup is allowed to open.
   */
  #canShow() {
    if (this.#readGlobal().subscribed) return false;

    const state = this.#read();

    if (!state.dismissedAt) return true;
    if (this.frequency <= 0) return true;

    return Date.now() - state.dismissedAt >= this.frequency * DAY_IN_MS;
  }

  /**
   * @returns {{ subscribed?: boolean, dismissedAt?: number }} The stored state for this page type.
   */
  #read() {
    try {
      const pageState = JSON.parse(localStorage.getItem(this.#storageKey) ?? '{}');

      if (pageState.dismissedAt || this.pageType !== 'index') return pageState;

      // Older versions stored dismissals in one key for every page. Keep that on the homepage only.
      const legacy = JSON.parse(localStorage.getItem(this.#legacyStorageKey) ?? '{}');
      if (legacy.dismissedAt) return { dismissedAt: legacy.dismissedAt };

      return pageState;
    } catch {
      return {};
    }
  }

  /**
   * @returns {{ subscribed?: boolean }} Shop-wide popup state, shared across page types.
   */
  #readGlobal() {
    try {
      const globalState = JSON.parse(localStorage.getItem(this.#globalStorageKey) ?? '{}');
      const legacy = JSON.parse(localStorage.getItem(this.#legacyStorageKey) ?? '{}');

      return { ...legacy, ...globalState };
    } catch {
      return {};
    }
  }

  /**
   * @param {{ subscribed?: boolean, dismissedAt?: number }} patch - The state to merge in.
   */
  #remember(patch) {
    try {
      if (patch.subscribed) {
        localStorage.setItem(this.#globalStorageKey, JSON.stringify({ ...this.#readGlobal(), subscribed: true }));
      }

      if (patch.dismissedAt) {
        localStorage.setItem(this.#storageKey, JSON.stringify({ ...this.#read(), dismissedAt: patch.dismissedAt }));
      }
    } catch {
      // Storage is unavailable (private mode, quota) — the popup just shows again next visit.
    }
  }

  #handleDialogClose = () => {
    if (isDesignMode()) {
      removeSession(this.#editorKey);
      return;
    }

    this.#remember({ dismissedAt: Date.now() });
  };

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (/** @type {CustomEvent} */ (event).detail?.sectionId !== this.sectionId) return;

    this.#revealed = false;
    this.#reveal();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (/** @type {CustomEvent} */ (event).detail?.sectionId !== this.sectionId) return;

    this.closeDialog();
    this.#revealed = false;
  };

  /**
   * The component refs, narrowed to the elements this popup owns.
   *
   * @returns {Refs} The refs.
   */
  get #popupRefs() {
    return /** @type {Refs} */ (/** @type {unknown} */ (this.refs));
  }

  get #storageKey() {
    return `${STORAGE_PREFIX}${this.sectionId}:${this.pageType}`;
  }

  get #legacyStorageKey() {
    return `${STORAGE_PREFIX}${this.sectionId}`;
  }

  get #globalStorageKey() {
    return `${STORAGE_PREFIX}${this.sectionId}:global`;
  }

  get #editorKey() {
    return `${STORAGE_PREFIX}editor:${this.sectionId}`;
  }

  get sectionId() {
    return this.dataset.sectionId ?? '';
  }

  get pageType() {
    return this.dataset.pageType || 'page';
  }

  get trigger() {
    return this.dataset.trigger ?? 'delay';
  }

  get delay() {
    return Number(this.dataset.delay ?? 0) * 1000;
  }

  get scrollDepth() {
    return Number(this.dataset.scrollDepth ?? 0);
  }

  get frequency() {
    return Number(this.dataset.frequency ?? 0);
  }

  get showOnMobile() {
    return this.dataset.showOnMobile !== 'false';
  }

  get testMode() {
    return this.dataset.testMode === 'true';
  }

  /**
   * Whether Shopify rendered this page as the result of a successful signup. The marker lives
   * inside the form because `form.posted_successfully?` only exists within the form tag.
   *
   * @returns {boolean} True when the form was just submitted successfully.
   */
  get posted() {
    return Boolean(this.querySelector('[data-posted-marker]'));
  }
}

/**
 * @returns {number} How far down the page the visitor has scrolled, as a percentage.
 */
function scrollProgress() {
  const scrollable = document.documentElement.scrollHeight - window.innerHeight;

  if (scrollable <= 0) return 100;

  return (window.scrollY / scrollable) * 100;
}

/**
 * @param {string} key - The session storage key.
 * @returns {string | null} The stored value, or null when storage is unavailable.
 */
function readSession(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {string} key - The session storage key.
 * @param {string} value - The value to store.
 */
function writeSession(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage is unavailable — the thank you message is skipped after the reload.
  }
}

/**
 * @param {string} key - The session storage key.
 */
function removeSession(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to clean up.
  }
}

if (!customElements.get('newsletter-popup-component')) {
  customElements.define('newsletter-popup-component', NewsletterPopupComponent);
}
